/**
 * PropertyCheckbox Template
 *
 * Specialized checkbox component for property table selection
 * Supports indeterminate state for "select all" functionality
 * Optimized for table row selection patterns
 */

"use client";

import React from "react";

import { Checkbox } from "@nextui-org/react";

import { cn } from "@/utils/cn";

export interface PropertyCheckboxProps {
  /** Checked state */
  isSelected: boolean;
  /** Indeterminate state (for select all) */
  isIndeterminate?: boolean;
  /** Change handler */
  onSelectionChange: (checked: boolean) => void;
  /** Disabled state */
  isDisabled?: boolean;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  /** Color scheme */
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  /** Custom className */
  className?: string;
  /** Accessibility label */
  "aria-label"?: string;
  /** Custom icon for checked state */
  icon?: React.ReactNode;
  /** Custom icon for indeterminate state */
  indeterminateIcon?: React.ReactNode;
  /** Show hover effects */
  showHover?: boolean;
  /** Compact mode (smaller padding) */
  compact?: boolean;
}

export function PropertyCheckbox({
  isSelected,
  isIndeterminate = false,
  onSelectionChange,
  isDisabled = false,
  size = "sm",
  color = "default",
  className,
  "aria-label": ariaLabel,
  icon,
  indeterminateIcon,
  showHover = true,
  compact = false,
}: PropertyCheckboxProps) {
  const handleChange = (checked: boolean) => {
    if (!isDisabled) {
      onSelectionChange(checked);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-center",
        "min-w-[24px] min-h-[24px]", // Container padding around checkbox
        "transition-colors duration-150 ease-in-out",
        className,
      )}
    >
      <Checkbox
        isSelected={isSelected}
        isIndeterminate={isIndeterminate}
        onValueChange={handleChange}
        isDisabled={isDisabled}
        size={size}
        aria-label={ariaLabel}
        icon={icon}
        data-avezor-checkbox="true"
        data-state={
          isIndeterminate
            ? "indeterminate"
            : isSelected
              ? "checked"
              : "unchecked"
        }
        classNames={{
          base: cn(
            "inline-flex m-0 bg-transparent hover:bg-transparent",
            "data-[hover=true]:bg-transparent",
            "avezor-checkbox-base",
          ),
          wrapper: cn(
            // Figma specifications: 20x20px with 6px border radius
            "w-[20px] h-[20px] min-w-[20px] min-h-[20px]",
            "rounded-[6px]",
            // Default state
            "border-2 border-[#D1D5DB] bg-white",
            // Hover state
            "hover:border-[#CF1409] hover:bg-[#FEF2F2]",
            // Selected state
            "data-[selected=true]:border-[#CF1409] data-[selected=true]:bg-[#CF1409]",
            // Indeterminate state
            isIndeterminate && "border-[#CF1409] bg-[#CF1409]",
            "transition-all duration-150 ease-in-out",
            "avezor-checkbox-wrapper",
          ),
          icon: cn(
            "w-[12px] h-[12px]", // Figma icon size
            "text-white",
            "avezor-checkbox-icon",
          ),
        }}
      >
        {/* Custom indeterminate icon */}
        {isIndeterminate && indeterminateIcon ? (
          <span className="flex items-center justify-center w-full h-full">
            {indeterminateIcon}
          </span>
        ) : null}
      </Checkbox>
    </div>
  );
}

export default PropertyCheckbox;
